/* Product page behaviour: the spectrum switcher, the quantity stepper, the
   anatomy pin/legend pairing, and the buy bar that arms once the real add
   button has scrolled away.

   Everything degrades: without JS the first spectrum panel is the one shown,
   the quantity field is a plain number input, the legend is a plain list,
   and the buy bar simply never appears. */
(function () {
  'use strict';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- spectrum ------------------------------------------------------- */

  function initSpectrum(root) {
    var tabs = [].slice.call(root.querySelectorAll('[data-ak-spectrum-tab]'));
    var panels = [].slice.call(root.querySelectorAll('[data-ak-spectrum-panel]'));
    if (!tabs.length) return;

    function select(curve, focus) {
      root.dataset.mode = curve;
      tabs.forEach(function (t) {
        var on = t.dataset.akSpectrumTab === curve;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
        if (on && focus) t.focus();
      });
      panels.forEach(function (p) {
        p.hidden = p.dataset.akSpectrumPanel !== curve;
      });
    }

    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () {
        select(tab.dataset.akSpectrumTab, false);
      });
      tab.addEventListener('keydown', function (e) {
        var dir = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1
                : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1
                : e.key === 'Home' ? 'first'
                : e.key === 'End' ? 'last' : 0;
        if (!dir) return;
        e.preventDefault();
        var next = dir === 'first' ? 0
                 : dir === 'last' ? tabs.length - 1
                 : (i + dir + tabs.length) % tabs.length;
        select(tabs[next].dataset.akSpectrumTab, true);
      });
    });
  }

  /* ---- quantity stepper ----------------------------------------------- */

  function initQty(form) {
    var input = form.querySelector('[data-ak-pdp-qty]');
    if (!input) return;
    form.querySelectorAll('[data-ak-pdp-step]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var step = Number(btn.dataset.akPdpStep) || 1;
        var next = Math.max(1, (parseInt(input.value, 10) || 1) + step);
        input.value = String(next);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
    input.addEventListener('change', function () {
      var n = parseInt(input.value, 10);
      if (!isFinite(n) || n < 1) input.value = '1';
    });
  }

  /* ---- anatomy: legend and pins highlight together --------------------- */

  function initAnatomy(root) {
    var items = [].slice.call(root.querySelectorAll('[data-ak-anat-item]'));
    if (!items.length) return;

    function mark(index, on) {
      var pin = root.querySelector('[data-ak-anat-pin="' + index + '"]');
      var item = root.querySelector('[data-ak-anat-item="' + index + '"]');
      if (pin) pin.classList.toggle('is-lit', on);
      if (item) item.classList.toggle('is-lit', on);
    }

    items.forEach(function (item) {
      var i = item.dataset.akAnatItem;
      ['mouseenter', 'focus'].forEach(function (ev) {
        item.addEventListener(ev, function () { mark(i, true); });
      });
      ['mouseleave', 'blur'].forEach(function (ev) {
        item.addEventListener(ev, function () { mark(i, false); });
      });
    });
  }

  /* ---- buy bar --------------------------------------------------------- */

  function initBuyBar() {
    var bar = document.querySelector('[data-ak-buybar]');
    var anchor = document.querySelector('[data-ak-pdp-add]');
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;

    var shown = false;
    function show(on) {
      if (on === shown) return;
      shown = on;
      if (on) {
        bar.hidden = false;
        // let the browser paint the hidden state before transitioning in
        requestAnimationFrame(function () { bar.classList.add('is-up'); });
      } else {
        bar.classList.remove('is-up');
        if (reducedMotion) {
          bar.hidden = true;
        } else {
          window.setTimeout(function () { if (!shown) bar.hidden = true; }, 280);
        }
      }
    }

    new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        // arm only once the button has gone past the top, never before the
        // reader has reached it
        show(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      });
    }, { threshold: 0 }).observe(anchor);
  }

  /* ---- the upgrade selection -------------------------------------------
     A ticked box means the upgrade rides along with whichever add button
     gets pressed. The listener runs on the form in the CAPTURE phase and
     stops propagation, so the cart's own document-level submit handler
     never also fires: one request, two lines, no race. */

  function initBundle() {
    // the same offer can appear more than once (the buy column and the
    // section that explains it), so they all move together
    var toggles = [].slice.call(document.querySelectorAll('[data-ak-bundle-toggle]'));
    var extraEls = [].slice.call(document.querySelectorAll('[data-ak-bundle-extra]'));
    if (!toggles.length && !extraEls.length) return;
    /* A page can carry optional extras and no bundle at all, so the primary
       offer is allowed to be absent. A stand-in that is never checked keeps
       every branch below readable instead of guarding each one. */
    var toggle = toggles[0] || { checked: false, dataset: {}, value: '' };

    var buttons = [].slice.call(document.querySelectorAll('[data-ak-add-label]'));
    var barPrice = document.querySelector('[data-ak-buybar-price]');
    var heroPrice = document.querySelector('[data-ak-hero-price]');
    var chip = document.querySelector('[data-ak-bundle-chip]');
    var barId = document.querySelector('.ak-buybar__id');
    if (chip && barId) barId.appendChild(chip);
    // Both totals are rendered by Liquid, so the money format is never
    // rebuilt in JS and stays right for whichever store this runs on.
    var singleTotal = barPrice ? barPrice.textContent : '';
    var bothTotal = toggle.dataset.totalBoth || singleTotal;
    /* The hero's own rendered total, for the page that has extras and NO
       bundle. Liquid only emits data-single/data-both when there is an
       upgrade to switch between, so without this the hero has no baseline
       to add an extra to and would sit still while the bar moved. */
    var heroSingle = heroPrice ? heroPrice.textContent : '';

    /* Optional extras are a different thing from the upgrade. The upgrade is
       the bundle: one product, priced together, and it owns the add button's
       wording. An extra just rides along as its own line and leaves the
       button alone. It still has to move the TOTAL though, or the page
       quotes one number while the cart charges another, which is the one
       thing this component exists to avoid. */
    var extras = extraEls;

    /* Liquid renders both headline totals, so the money format is never
       rebuilt for the common case and stays right for whichever store this
       runs on. Only an extra forces arithmetic.

       The format comes from the ELEMENT, not from `Shopify.money_format`,
       which is undefined on these storefronts and would have fallen back to a
       hardcoded dollar sign: right on the US store by luck and wrong on AU,
       which prices in A$. The hero was rendered with `money` and the buy bar
       with `money_with_currency`, so each carries the one that made it. */
    function money(cents, fmt) {
      var parts = (cents / 100).toFixed(2).split('.');
      var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return fmt
        .replace(/\{\{\s*amount\s*\}\}/g, whole + '.' + parts[1])
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, whole);
    }

    function extraCents() {
      return extras.reduce(function (sum, el) {
        return sum + (el.checked ? Number(el.dataset.cents) || 0 : 0);
      }, 0);
    }

    function total(el, fallback) {
      var add = extraCents();
      var base = Number(el.dataset.base) || 0;
      var fmt = el.dataset.fmt || '';
      if (!add || !base || fmt.indexOf('{{') < 0) return fallback;
      if (toggle.checked) base += Number(toggle.dataset.cents) || 0;
      return money(base + add, fmt);
    }

    /* How many products the button is about to add: the main one, the
       upgrade if ticked, and each distinct extra ticked. Quantity is units
       of the main product, not products, so it does not count. */
    function selected() {
      var n = 1 + (toggle.checked ? 1 : 0);
      var seen = {};
      extras.forEach(function (el) {
        if (!el.checked || seen[el.value]) return;
        seen[el.value] = true;
        n += 1;
      });
      return n;
    }

    function sync() {
      var n = selected();
      buttons.forEach(function (el) {
        /* A pre-order hands down a counted form and uses it for anything
           beyond one product; an ordinary page keeps its single/both pair
           and, as before, lets extras leave the wording alone. */
        el.textContent = el.dataset.multi && n > 1
          ? el.dataset.multi.replace('[count]', String(n))
          : (toggle.checked ? el.dataset.both : el.dataset.single);
      });
      if (barPrice) {
        var bar = toggle.checked ? bothTotal : singleTotal;
        barPrice.textContent = total(barPrice, bar);
        barPrice.classList.toggle('is-bundled', toggle.checked || !!extraCents());
      }
      if (chip) chip.hidden = !toggle.checked;
      if (heroPrice) {
        /* With a bundle, the baseline is whichever of the two Liquid totals
           the toggle selects. Without one, it is the total Liquid rendered.
           Either way an extra is added on top, because a headline price that
           does not move while the cart charges more is the one failure this
           component exists to avoid. */
        var hero = heroPrice.dataset.both
          ? (toggle.checked ? heroPrice.dataset.both : heroPrice.dataset.single)
          : heroSingle;
        heroPrice.textContent = total(heroPrice, hero);
      }
    }

    extras.forEach(function (el) { el.addEventListener('change', sync); });

    toggles.forEach(function (t) {
      t.addEventListener('change', function () {
        toggles.forEach(function (other) {
          if (other !== t) other.checked = t.checked;
        });
        sync();
      });
    });

    /* A link can arrive with the upgrade already chosen:
         ?upgrade=1            tick it
         ?upgrade=pod-core     tick it when the handle matches
         ?upgrade=0            leave it alone
       so a campaign or a Launchpad step can point straight at the bundle. */
    (function preselect() {
      var q = new URLSearchParams(window.location.search);
      var want = q.get('upgrade') || q.get('add');
      if (!want) return;
      want = want.toLowerCase();
      if (want === '0' || want === 'false' || want === 'no') return;
      var on = want === '1' || want === 'true' || want === 'yes' ||
               want === (toggle.dataset.handle || '').toLowerCase();
      if (!on) return;
      toggles.forEach(function (t) { t.checked = true; });
    })();

    sync();

    [].slice.call(document.querySelectorAll('form[action*="/cart/add"]')).forEach(function (form) {
      if (!form.closest('[data-ak-product]') && !form.closest('[data-ak-buybar]')) return;

      form.addEventListener('submit', function (e) {
        var riders = extras.filter(function (el) { return el.checked; });
        // ordinary single add, let it through
        if (!toggle.checked && !riders.length) return;
        var drawer = document.querySelector('ak-cart-drawer');
        if (!drawer || typeof drawer.addMany !== 'function') return;

        e.preventDefault();
        e.stopPropagation();

        var idField = form.querySelector('[name="id"]');
        var qtyField = form.querySelector('[name="quantity"]');
        var qty = parseInt(qtyField && qtyField.value, 10);

        /* A plain submit posts the form's hidden properties[...] inputs with
           it. This path builds the request by hand, so the main line has to
           carry them explicitly, or a pre-order bought with an extra ticked
           would reach the order without the promise the page made. */
        var props = {};
        var hasProps = false;
        [].slice.call(form.querySelectorAll('input[name^="properties["]')).forEach(function (el) {
          var m = /^properties\[(.+)\]$/.exec(el.name);
          if (!m) return;
          props[m[1]] = el.value;
          hasProps = true;
        });

        var main = { id: Number(idField.value), quantity: qty > 0 ? qty : 1 };
        if (hasProps) main.properties = props;
        var lines = [main];

        /* A tick box's own line carries whatever Liquid declared for it in
           data-properties (a pre-order promise, for one), the same way the
           main line carries the form's hidden inputs. */
        function rider(el) {
          var line = { id: Number(el.value), quantity: 1 };
          if (el.dataset.properties) {
            try { line.properties = JSON.parse(el.dataset.properties); } catch (err) { /* malformed: add it plain */ }
          }
          return line;
        }
        if (toggle.checked) lines.push(rider(toggle));
        /* The same extra can be offered twice on one page, so add each
           variant once however many boxes are ticked for it. */
        var seen = {};
        riders.forEach(function (el) {
          if (seen[el.value]) return;
          seen[el.value] = true;
          lines.push(rider(el));
        });

        drawer.flyFrom(form);
        drawer.addMany(lines, form.querySelector('[type="submit"]'));
      }, true);
    });
  }

  /* ---- boot ------------------------------------------------------------ */

  /* Autoplay is a motion decision, so it answers to the motion preference.
     Pausing leaves the poster frame on screen, which is the whole picture. */
  function initLoops() {
    if (!reducedMotion) return;
    document.querySelectorAll('[data-ak-loop]').forEach(function (v) {
      v.removeAttribute('autoplay');
      v.pause();
    });
  }

  /* The copepod swims one of four generated paths. CSS cannot pick at
     random, so the choice is made here: a path, and a small stretch or
     squeeze of its length on top, which is enough that two loads never look
     like the same loop. Path 1 is already set in CSS, so with JS off it
     still swims, it just always swims the same way. */
  function initSwim() {
    if (reducedMotion) return;
    document.querySelectorAll('.ak-mode__subject').forEach(function (el) {
      el.dataset.swim = String(1 + Math.floor(Math.random() * 4));
      el.style.setProperty('--swim-jitter', (0.88 + Math.random() * 0.26).toFixed(3));
      // start part way in, so it is already mid-journey when you arrive
      el.style.animationDelay = '-' + (Math.random() * 6).toFixed(2) + 's';
    });
  }

  function boot() {
    document.querySelectorAll('[data-ak-spectrum]').forEach(initSpectrum);
    document.querySelectorAll('[data-ak-pdp-form]').forEach(initQty);
    document.querySelectorAll('.ak-anat').forEach(initAnatomy);
    initBuyBar();
    initBundle();
    initLoops();
    initSwim();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
